import React from 'react';
import { useTenant } from '../context/TenantContext';

export const PrivacyPolicy: React.FC = () => {
  const { tenant } = useTenant();
  const orgName = tenant?.name || 'Your Organisation';
  const lastUpdated = 'March 2026';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-10">Last updated: {lastUpdated}</p>

      <div className="prose prose-gray max-w-none space-y-8">
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Who We Are</h2>
          <p className="text-gray-700 leading-relaxed">
            KulaLearn is a learning management platform operated by ThinkShow (Pty) Ltd
            (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;). We act as an{' '}
            <strong>operator</strong> (as defined in the Protection of Personal Information
            Act, No. 4 of 2013 &ndash; &ldquo;POPIA&rdquo;) on behalf of {orgName}{' '}
            (&ldquo;the Organisation&rdquo;), who is the <strong>responsible party</strong>{' '}
            for your personal information.
          </p>
          <p className="text-gray-700 leading-relaxed mt-3">
            This means {orgName} determines why and how your personal information is
            processed. We process it only as instructed by the Organisation and in
            accordance with our agreement with them.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">2. What Data We Collect</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            We collect and process only the minimum personal information required to
            deliver the platform:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li><strong>Account information:</strong> name and email address</li>
            <li><strong>Learning data:</strong> course progress, quiz scores, and activity responses</li>
            <li><strong>Payment references:</strong> transaction amounts, currency, and payment gateway reference numbers (we do not store card details)</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-3">
            We do not collect phone numbers, physical addresses, identity numbers, or
            biometric data.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Data</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            Your personal information is used exclusively for the purposes of:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li>Providing access to courses and learning materials</li>
            <li>Tracking your course progress and issuing completion certificates</li>
            <li>Processing course enrolment and payments</li>
            <li>Sending transactional emails (e.g. enrolment confirmation, password resets)</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-3">
            We do not sell or share your personal information with third parties for
            marketing purposes. We do not use your data for profiling or automated
            decision-making.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Legal Basis</h2>
          <p className="text-gray-700 leading-relaxed">
            We process your personal information on the basis of{' '}
            <strong>contractual necessity</strong> (POPIA section 11(1)(b)) &ndash; it is
            necessary to deliver the learning services you or your Organisation have
            enrolled in.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Children&rsquo;s Data</h2>
          <p className="text-gray-700 leading-relaxed">
            Where a user is younger than 18 years of age, the Organisation is responsible
            for obtaining the necessary consent from the user&rsquo;s parent or guardian
            prior to their use of the platform, as required by POPIA section 35. We do not
            knowingly collect personal information from children without organisational
            authorisation.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Third-Party Infrastructure</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            The platform uses third-party cloud infrastructure which may involve the
            storage or processing of data on servers located outside the Republic of South
            Africa. We use our best endeavours to ensure that these providers maintain
            appropriate security standards.
          </p>
          <p className="text-gray-700 leading-relaxed mb-2">Our sub-processors include:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li><strong>Supabase</strong> &ndash; database, authentication, and file storage</li>
            <li><strong>Vercel</strong> &ndash; application hosting and content delivery</li>
            <li><strong>Resend</strong> &ndash; transactional email delivery</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Your Rights</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            Under POPIA, you have the right to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li>Request access to the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Object to the processing of your personal information</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-3">
            To exercise these rights, please contact your Organisation administrator
            ({orgName}), who will coordinate with us to fulfil your request. If your
            account is deleted, all associated data &ndash; including enrolments, quiz
            attempts, activity responses, and submissions &ndash; is permanently removed.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Data Security</h2>
          <p className="text-gray-700 leading-relaxed">
            We implement and maintain reasonable technical and organisational measures to
            protect personal information against loss, damage, unauthorised access, or
            unlawful processing. These measures include encrypted data transmission,
            row-level security policies, and authenticated access controls.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Changes to This Policy</h2>
          <p className="text-gray-700 leading-relaxed">
            We may update this Privacy Policy from time to time to reflect changes in our
            practices or legal requirements. Material changes will be communicated through
            the platform or via your Organisation.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Contact</h2>
          <p className="text-gray-700 leading-relaxed">
            For privacy-related queries, please contact your Organisation administrator
            ({orgName}) in the first instance. For platform-level queries, contact
            ThinkShow (Pty) Ltd at{' '}
            <a href="mailto:hello@thinkshow.co" className="text-blue-600 hover:underline">
              hello@thinkshow.co
            </a>.
          </p>
        </section>
      </div>
    </div>
  );
};
